<template>
  <div :class="{'spl-parcel-filter--return': !localFilter.Show_Forward_Flow}" class="spl-parcel-filter">
    <div
      v-if="!expanded"
      class="spl-parcel-filter__collapsed">
      <button
        :disabled="searchActive"
        class="spl-parcel-filter__text"
        tabindex="1"
        role="button"
        style="grid-column: 1"
        @click="toggleExpand">
        <p>{{ filterText }}</p>
      </button>
      <button
        :disabled="searchActive"
        tabindex="2"
        class="spl-parcel-filter__icon-button spl-parcel-filter__edit-filter"
        style="grid-column: 2"
        @click="toggleExpand">
        <img src="../../assets/icon-edit.svg">
      </button>
      <button
        :disabled="searchActive"
        tabindex="3"
        class="spl-parcel-filter__icon-button spl-parcel-filter__refresh"
        style="grid-column: 3"
        @click="handleReload">
        <img src="../../assets/icon-restore.svg">
      </button>
    </div>
    <div v-else>
      <div class="spl-parcel-filter__date">
        <div class="spl-parcel-filter__date--from">
          <form-label>From ({{ fromWeekNumber }})</form-label>
          <form-datepicker v-model="dateFrom" />
        </div>
        <div class="spl-parcel-filter__date--to">
          <form-label>To ({{ toWeekNumber }})</form-label>
          <form-datepicker v-model="dateTo" />
        </div>
        <form-button class="spl-parcel-filter__date--today" @onClick="handleSelectTime('today')">
          Today
        </form-button>
        <form-button class="spl-parcel-filter__date--last-day" @onClick="handleSelectTime('lastWorkingDay')">
          Last Day
        </form-button>
        <form-button class="spl-parcel-filter__date--last-week" @onClick="handleSelectTime('lastWeek')">
          Last Week
        </form-button>
        <form-button class="spl-parcel-filter__date--last-month" @onClick="handleSelectTime('lastMonth')">
          Last Month
        </form-button>
      </div>
      <div class="spl-parcel-filter__multiselects">
        <div class="spl-parcel-filter__multiselects--country">
          <form-label>Country</form-label>
          <country-multiselect
            :selected-keys="localFilter.Countries"
            placeholder="Countries"
            @valuesSelected="onCountriesSelected" />
        </div>
        <div class="spl-parcel-filter__multiselects--customer">
          <form-label>Customer</form-label>
          <customer-multiselect
            :selected-keys="localFilter.Customers"
            placeholder="Customers"
            @valuesSelected="onCustomersSelected" />
        </div>
        <div class="spl-parcel-filter__multiselects--parcel-status">
          <form-label>Parcel Status</form-label>
          <form-filtered-multiselect
            v-model="localFilter.Parcel_Status"
            :options="constants.PARCEL_STATUS_OPTIONS"
            track-by="value"
            label="label"
            placeholder="Parcel Status" />
        </div>
        <div class="spl-parcel-filter__multiselects--service-level">
          <form-label>Service Level</form-label>
          <form-filtered-multiselect
            v-model="localFilter.Service_Levels"
            :options="serviceLevelOptions"
            track-by="value"
            label="label"
            placeholder="Service Level" />
        </div>
        <div class="spl-parcel-filter__multiselects--current-event">
          <form-label>Current Event</form-label>
          <form-filtered-multiselect
            v-model="localFilter.Current_Event_Infos"
            :options="eventInfoOptions"
            track-by="value"
            label="label"
            placeholder="Current Event" />
        </div>
        <div
          v-if="userHasPermission(constants.PERMISSION_TYPES.PROVIDER, constants.PERMISSION_VALUES.LIST)"
          class="spl-parcel-filter__multiselects--provider">
          <form-label>Provider</form-label>
          <provider-multiselect
            :selected-keys="localFilter.Providers"
            placeholder="Providers"
            @valuesSelected="onProvidersSelected" />
        </div>

        <div
          v-if="userHasPermission(constants.PERMISSION_TYPES.TAG, constants.PERMISSION_VALUES.LIST)"
          class="spl-parcel-filter__multiselects--tag">
          <form-label>Tag</form-label>
          <tag-multiselect
            :selected-keys="localFilter.Tags"
            placeholder="Tags"
            @valuesSelected="onTagsSelected" />
        </div>
      </div>

      <div class="spl-parcel-filter__other">
        <div class="spl-parcel-filter__other--postal-from">
          <form-label>Postal Code From</form-label>
          <form-input v-model="localFilter.From_Postal_Code" />
        </div>
        <div class="spl-parcel-filter__other--postal-to">
          <form-label>Postal Code To</form-label>
          <form-input v-model="localFilter.To_Postal_Code" />
        </div>
      </div>
      <div class="spl-parcel-filter__flow">
        <form-label>Show Flow</form-label>
        <form-toggle-switch
          v-model="localFilter.Show_Forward_Flow"
          :pre-selected="true"
          text-unchecked="Return"
          text-checked="Forward" />
      </div>
      <div class="spl-parcel-filter__buttons">
        <form-button class="spl-parcel-filter__apply" @onClick="finishEditing">
          Apply
        </form-button>
        <form-button class="spl-parcel-filter__clear" @onClick="clearFilter">
          Clear
        </form-button>
        <form-button class="spl-parcel-filter__default" @onClick="setDefaultFilter">
          Default
        </form-button>
        <form-button
          :secondary="true"
          class="spl-parcel-filter__cancel"
          @onClick="handleCancel">
          Cancel
        </form-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import moment from 'moment'
import constants from '@/constants'
import Helpers from '@/helpers.vue'
import { getLastWorkingDay } from '@/helper'

export default {
  mixins: [Helpers],
  props: ['filterName'],
  data () {
    return {
      dateFormat: 'dd.MM.yyyy',
      dateFrom: moment().toISOString(true),
      dateTo: moment().toISOString(true),
      localFilter: {
        Countries: [],
        Current_Event_Infos: [],
        Customers: [],
        Providers: [],
        Tags: [],
        From_Date: '',
        To_Date: '',
        Parcel_Status: [],
        Service_Levels: [],
        Show_Forward_Flow: true,
        From_Postal_Code: '',
        To_Postal_Code: ''
      },
      expanded: false,
      unsubscribeParcelFilter: null,
      searchTerm: '',
      constants
    }
  },
  computed: {
    ...mapGetters('parcelFilter', ['parcelFilter']),
    ...mapGetters('serviceLevel', ['serviceLevelOptions']),
    ...mapState('eventInfo', ['eventInfoOptions']),
    ...mapGetters('country', ['countryNames']),
    ...mapGetters('customer', ['customerNames']),
    ...mapGetters('provider', ['providerNames']),
    ...mapGetters('tag', ['tagNames']),
    ...mapGetters('serviceLevel', ['serviceLevelNames']),
    ...mapGetters('eventInfo', ['eventInfoNames']),
    ...mapGetters('personalSettings', ['defaultCountries',
      'defaultCustomers',
      'defaultProviders',
      'defaultTags',
      'defaultDate',
      'defaultGrouping',
      'defaultParcelStatus',
      'defaultServiceLevels',
      'defaultEvents',
      'defaultPostalCodeFrom',
      'defaultPostalCodeTo',
      'defaultFlow']),
    filterText () {
      var text:string = 'Filter - '
      text += 'Date: ' + moment(this.dateFrom).format('YYYY-MM-DD') + ' - ' + moment(this.dateTo).format('YYYY-MM-DD')
      if (this.localFilter.Countries && this.localFilter.Countries.length > 0) {
        text += ' | Countries (' + this.localFilter.Countries.length + ')'
      }
      if (this.localFilter.Customers && this.localFilter.Customers.length > 0) {
        text += ' | Customers ('+ this.localFilter.Customers.length + ')'
      }
      if (this.localFilter.Providers && this.localFilter.Providers.length > 0) {
        text += ' | Providers ('+ this.localFilter.Providers.length + ')'
      }
      if (this.localFilter.Tags && this.localFilter.Tags.length > 0) {
        text += ' | Tags ('+ this.localFilter.Tags.length + ')'
      }
      if (this.localFilter.Current_Event_Infos && this.localFilter.Current_Event_Infos.length > 0) {
        text += ' | Event Infos (' + this.localFilter.Current_Event_Infos.length + ')'
      }
      if (this.localFilter.Service_Levels && this.localFilter.Service_Levels.length > 0) {
        text += ' | Service Levels (' + this.localFilter.Service_Levels.length + ')'
      }
      if (this.localFilter.Parcel_Status && this.localFilter.Parcel_Status.length > 0) {
        text += ' | Status (' + this.localFilter.Parcel_Status.length + ')'
      }
      if (this.localFilter.From_Postal_Code || this.localFilter.To_Postal_Code) {
        text += ' | Postal Code: ' + this.localFilter.From_Postal_Code + ' - ' + this.localFilter.To_Postal_Code
      }
      text += ' | Flow: ' + (this.localFilter.Show_Forward_Flow ? 'Forward' : 'Return')
      return text
    },
    fromWeekNumber () {
      var currentdate: any = moment(this.dateFrom).toDate()
      var startDate: any = new Date(currentdate.getFullYear(), 0, 1)
      var numberOfDays = Math.floor((currentdate - startDate) / (24 * 60 * 60 * 1000))
      var result = Math.ceil((currentdate.getDay() + 1 + numberOfDays) / 7)
      var fromWeekNumber:string = 'Week Number: ' + result
      return fromWeekNumber
    },
    toWeekNumber () {
      var currentdate: any = moment(this.dateTo).toDate()
      var startDate: any = new Date(currentdate.getFullYear(), 0, 1)
      var numberOfDays = Math.floor((currentdate - startDate) / (24 * 60 * 60 * 1000))
      var result = Math.ceil((currentdate.getDay() + 1 + numberOfDays) / 7)
      var fromWeekNumber:string = 'Week Number: ' + result
      return fromWeekNumber
    },
    searchActive () {
      return this.$route.query.search !== undefined
    }
  },
  watch: {
    dateFrom: function (newVal, oldVal) {
      this.localFilter.From_Date = moment(newVal).format()
      if (moment(this.dateFrom).unix() > moment(this.dateTo).unix()) {
        this.dateTo = moment(this.dateFrom).toISOString(true)
      }
    },
    dateTo: function (newVal, oldVal) {
      this.localFilter.To_Date = moment(newVal).format()
      if (moment(this.dateFrom).unix() > moment(this.dateTo).unix()) {
        this.dateFrom = moment(this.dateTo).toISOString(true)
      }
    }
  },
  beforeDestroy () {
    this.unsubscribeParcelFilter()
  },
  created () {
    if (this.$route.params.from_date) {
      this.setParcelFilterFromRoute({ routeFilter: this.$route.params, filterName: this.filterName })
    }
    this.localFilter = this.parcelFilter(this.filterName)
    this.dateTo = moment(this.localFilter.To_Date).toISOString(true)
    this.dateFrom = moment(this.localFilter.From_Date).toISOString(true)
    this.unsubscribeParcelFilter = this.$store.subscribe(this.handleParcelFilterMutation)
    if (this.$route.query.search) {
      this.searchTerm = this.$route.query.search
    }
  },
  methods: {
    ...mapMutations('parcelFilter', ['setParcelFilter']),
    ...mapActions('parcelFilter', ['setParcelFilterFromRoute']),
    handleParcelFilterMutation (mutation, state) {
      if (mutation.type === 'parcelFilter/setParcelFilter') {
        this.localFilter = this.parcelFilter(this.filterName)
        this.dateFrom = moment(this.localFilter.From_Date).toISOString(true)
        this.dateTo = moment(this.localFilter.To_Date).toISOString(true)
      }
    },
    toggleExpand () {
      this.expanded = !this.expanded
    },
    handleCancel () {
      this.localFilter = this.parcelFilter(this.filterName)
      this.toggleExpand()
    },
    finishEditing () {
      this.setCurrentFilter(this.localFilter)
      if (!this.useReport) {
        this.update_URL(this.$route.name, this.localFilter)
      }
      this.toggleExpand()
    },
    handleSelectTime (time) {
      switch (time) {
        case 'today':
          this.dateFrom = moment().toISOString(true)
          this.dateTo = moment().toISOString(true)
          break
        case 'lastWorkingDay':
          this.dateFrom = getLastWorkingDay().toISOString(true)
          this.dateTo = moment().subtract(1, 'day').toISOString(true)
          break
        case 'lastWeek':
          this.dateFrom = moment().startOf('week').subtract(1, 'week').add(1, 'day').toISOString(true)
          this.dateTo = moment().startOf('week').toISOString(true)
          break
        case 'lastMonth':
          this.dateFrom = moment().startOf('month').subtract(1, 'month').toISOString(true)
          this.dateTo = moment().startOf('month').subtract(1, 'day').toISOString(true)
          break
      }
    },
    onCountriesSelected (countries) {
      this.localFilter.Countries = countries
    },
    onCustomersSelected (customers) {
      this.localFilter.Customers = customers
    },
    onProvidersSelected (providers) {
      this.localFilter.Providers = providers
    },
    onTagsSelected (tags) {
      this.localFilter.Tags = tags
    },
    onStatusSelected (parcelStataus) {
      this.localFilter.Parcel_Status = parcelStataus
    },
    onServiceLevelSelected (serviceLevel) {
      this.localFilter.Service_Levels = serviceLevel
    },
    onEventSelected (event) {
      this.localFilter.Current_Event_Infos = event
    },
    clearFilter () {
      this.localFilter = {
        Countries: [],
        Current_Event_Infos: [],
        Customers: [],
        Providers: [],
        Tags: [],
        From_Date: this.formatDate(new Date(), false),
        To_Date: this.formatDate(new Date(), false),
        Parcel_Status: [],
        Service_Levels: [],
        Show_Forward_Flow: true,
        From_Postal_Code: '',
        To_Postal_Code: ''
      }
      this.setCurrentFilter(this.localFilter)
    },
    setDefaultFilter () {
      let fromDate = moment()
      if (this.defaultDate === -1) {
        fromDate = getLastWorkingDay()
      }

      this.localFilter = {
        Countries: this.defaultCountries,
        Current_Event_Infos: this.defaultEvents,
        Customers: this.defaultCustomers,
        Providers: this.defaultCustomers,
        Tags: this.defaultTags,
        From_Date: fromDate,
        To_Date: moment().add(this.defaultDate, 'days').format(),
        Parcel_Status: this.defaultParcelStatus,
        Service_Levels: this.defaultServiceLevels,
        Show_Forward_Flow: this.defaultFlow === 'Forward',
        From_Postal_Code: this.defaultPostalCodeFrom,
        To_Postal_Code: this.defaultPostalCodeTo
      }
      this.setCurrentFilter(this.localFilter)
    },
    setCurrentFilter (filter) {
      this.setParcelFilter({ parcelFilter: filter, filterName: this.filterName })
    },
    handleReload () {
      this.$emit('onReload')
    }
  }
}
</script>

<style lang="scss">
@import '../../variables.scss';

.spl-parcel-filter {
  background-color: $yellowButtonLabelHover;
  position: relative;
  padding: 0 $mainPadding;
  margin-bottom: 15px;
  &--return {
    background: $darkRed;
  }
  &__date {
    display: grid;
    padding: 20px 0;
    width: 100%;
    grid-template-columns: 220px 220px 60px 74px 87px 83px;
    grid-gap: 10px;
    &--from {
      grid-column: 1;
    }
    &--to {
      grid-column: 2;
    }
    &--today {
      grid-column: 3;
    }
    &--last-day {
      grid-column: 4;
    }
    &--last-week {
      grid-column: 5;
    }
    &--last-month {
      grid-column: 6;
    }

    .spl-button {
      margin: 22px 0 0 0;
      padding: 0px;
    }
  }

  &__multiselects {
    display: grid;
    padding: 20px 0;
    width: 100%;
    grid-template-columns: minmax(250px, 2000px)
    minmax(250px, 2000px)
    minmax(250px, 2000px);

    &--country {
      grid-column: 1;
      grid-row: 1;
    }

    &--customer {
      grid-column: 2;
      grid-row: 1;
    }

    &--parcel-status {
      grid-column: 3;
      grid-row: 1;
    }

    &--service-level {
      grid-column: 1;
      grid-row: 2;
    }

    &--current-event {
      grid-column: 2;
      grid-row: 2;
    }
    &--provider {
      grid-column: 3;
      grid-row: 2;
    }
    &--tag {
      grid-column: 1;
      grid-row: 3;
    }

    grid-gap: 10px;
    .spl-button {
      margin: 22px 7px 0 0;
      padding: 0px 10px;
    }
  }

  &__other {
    display: grid;
    padding: 20px 0;
    width: 100%;
    grid-template-columns: 150px 150px 100px
    minmax(250px, 2000px)
    minmax(250px, 2000px);
    grid-gap: 10px;
    &--postal-from {
      grid-column: 1;
    }
    &--postal-to {
      grid-column: 2;
    }
  }

  &__collapsed {
    display: grid;
    grid-template-columns: auto 50px 50px;
    grid-gap: 10px;
    padding: 10px 0;
    .spl-button {
      margin: 0;
    }
    input {
      margin: 0;
    }
  }

  &__icon-button {
    cursor: pointer;
    background: #111;
    border: 0;
    border-radius: 5px;
    &:focus {
      background-color: #222;
    }
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  &__text {
    width: 100%;
    cursor: pointer;
    background: 0;
    border: 0;
    text-align: left;
    color: white;
    height: 40px;
    font-family: "Open Sans", sans-serif;
    font-weight: 400;
    font-size: 13px;
    p {
      margin: 0;
    }
    &:focus {
      background-color: grey;
    }
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
  &__flow {
    margin-bottom: 35px;
  }
  &__apply, &__clear, &__default, &__cancel  {
    width: 150px;
    margin-bottom: 20px
  }
}

@media (max-width: 800px) {
  .spl-parcel-filter {
    &__date,  &__mutliselects,  &__other {
      grid-template-columns: 100%;
    }
  }
}

@include mobile() {
  .spl-parcel-filter {
    padding: 3px;

    &__refresh, &__search-input, &__search {
      display: none;
    }
    &__edit {
      margin-right: 5px;
    }
    &__collapsed {
      display: flex;
    }

    &__icon-button {
      width: 40px;
    }
    &__text {
      width: calc(100% - 50px);
    }
    &__date {
      display: inline;
      &--from {
        & .el-input {
          width: 100% !important;
        }
        grid-area: a
      }
      &--to {
        & .el-input {
          width: 100% !important;
        }
        grid-area: b
      }

      & .spl-button {
        margin: 5px;
        height: 35px;
        width: calc(50% - 10px);
        font-size: 14px;
      }
    }

    &__multiselects {
      grid-template-columns: 100%;
      & .spl-multiselect {
        margin-bottom: 5px;
      }
      &--country {
        grid-column: 1;
        grid-row: 1;
      }

      &--customer {
        grid-column: 1;
        grid-row: 2;
      }

      &--parcel-status {
        grid-column: 1;
        grid-row: 3;
      }

      &--service-level {
        grid-column: 1;
        grid-row: 4;
      }

      &--current-event {
        grid-column: 1;
        grid-row: 5;
      }
      &--provider {
        grid-column: 1;
        grid-row: 6;
      }
      &--tag {
        grid-column: 1;
        grid-row: 7;
      }
    }
    &__other {
      & .spl-input {
        margin-bottom: 5px;
        height: 40px;
      }
      &--postal-from {
        grid-column: 1;
        grid-row: 1;
      }
      &--postal-to {
        grid-column: 1;
        grid-row: 2;
      }
    }
    &__apply, &__clear, &__default, &__cancel {
      width: auto;
      margin: 0;
      padding: 5px 5px;
    }
    &__buttons {
      display: inline;
      & .spl-button {
        width: calc(50% - 10px);
        margin: 5px;
        height: 39px;
        font-size: 15px;
      }
    }
  }
}
</style>
